import IconsInstance from "../../../scripts/icons";
import { probeLabel } from '../../../util/probe-label';
import roundTo from "round-to";
import celsiusToFahrenheit from "../../../util/celsius-to-fahrenheit";
import getHeatIndex from "../../../util/get-heat-index";
import fahrenheitToCelsius from "../../../util/fahrenheit-to-celsius";
import isUndefined from "lodash.isundefined";
import {
  readingFrequencyLabels,
  describeReadingFrequency,
  readingFrequencySortValue,
} from "../../../util/reading-frequency";
import {
  formatPipeStrapLocation,
  formatSprinklerSystemType,
  formatZipCode,
} from "./questionnaire-columns";
import {
  responseFor,
  responseSortValue,
  renderResponseCell,
} from "./sensor-response";

// a reading in alarm pulses until someone has it in hand: on it, snoozed, or
// closed while it comes back
const HANDLED_STATES = ['working', 'snoozed', 'closed'];
const readingAlarmClass = (dashboard, item, readingType) => {
  const status = dashboard.getAlertStatus(item, readingType).status;
  const handled = status === dashboard.AlertStatus.ACTIVE && HANDLED_STATES.includes(responseFor(item).state);
  return `reading-value--${status}${handled ? ' reading-value--handled' : ''}`;
};

const escapeHtml = value => {
  if (window.escapeHTML) return window.escapeHTML(String(value));

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const questionnaireValue = value => value ? escapeHtml(value) : '&mdash;';

// The name an account gave itself in the Tempstick app -- a business
// profile's name, else the person's -- rides on every sensor row as
// owner_name. Selective's dashboard calls it the policyholder (its
// policyholders name their business in onboarding); every other enterprise
// sees it as the business name.
import { isPolicyHolderEnterprise } from '../../../util/enterprise-branding';
export { POLICY_HOLDER_ENTERPRISE_IDS } from '../../../util/enterprise-branding';

export const ownerNameLabel = () => (isPolicyHolderEnterprise() ? 'Policy Holder' : 'Business Name');

// an account named by its login: an address, not a name
const looksLikeEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * The name the owner_name column shows for a sensor's account, and whether
 * it is borrowed: {name, inherited}.
 *
 * Few policyholders name themselves in the app, which left Selective's
 * Policy Holder column a run of dashes. So where the name is missing, that
 * column borrows the Account name -- what the enterprise called the linked
 * account -- unless it is an email address, which says who logs in, not who
 * the policyholder is. It is a way of showing the row, in the dashboard
 * only: nothing is saved, the API still reports owner_name empty, and the
 * Business Name other enterprises see is left as it is.
 */
export const ownerNameFor = item => {
  const own = String(item.owner_name || '').trim();

  if (own) {
    return { name: own, inherited: false };
  }

  const account = String(item.account_name || '').trim();

  if (isPolicyHolderEnterprise() && account && !looksLikeEmail(account)) {
    return { name: account, inherited: true };
  }

  return { name: '', inherited: false };
};

const sensorData = dashboard => {

  return {
    sensor_id: {
      key: 'sensor_id',
      label: 'Sensor ID',
      sortValue: item => item.sensor_id,
      value: item => {
        return item.sensor_id;
      }
    },
    name: {
      key: 'name',
      label: 'Sensor Name',
      sortValue: item => item.sensor_name,
      value: item => {
        return `${item.sensor_name}`;
      }
    },
    response: {
      key: 'response',
      label: 'Alert Response',
      description: 'Where the response to this sensor\'s alerts stands: who is working on it, snoozes, and closes in the Resolution Center, or the alerts\' own state. Sorts what needs someone first',
      sortValue: item => responseSortValue(item),
      value: item => renderResponseCell(item),
    },
    created: {
      key: 'created',
      label: 'Activated',
      sortValue: item => item.created,
      value: item => {

        if(!item.created) {
          return '—';
        }

        const created = window.getUserTimezoneDate(item.created);

        return `
            <div style="text-align:left;" class="monospace-dt" title="Activated ${created.format('MMM D, YYYY h:mm a')}">${created.format('MM/DD/YY')}</div> 
          `;
      }
    },
    last_checkin: {
      key: 'last_checkin',
      label: 'Last Reading',
      sortValue: item => item.last_checkin,
      value: item => {
        return `
            <div style="text-align:left;" class="monospace-dt">${window.getUserTimezoneDate(item.last_checkin).format('MM/DD/YY')}</div> 
            <div style="text-align:right; width: 69px;" class="monospace-dt">${window.getUserTimezoneDate(item.last_checkin).format('h:mm a')}</div>
          `;
      }
    },
    next_checkin: {
      key: 'next_checkin',
      label: 'Next Reading',
      sortValue: item => item.next_checkin,
      value: item => {

        if(!item.next_checkin) {
          return '—';
        }

        const next = window.getUserTimezoneDate(item.next_checkin);
        const isOverdue = next.valueOf() < Date.now();

        const title = isOverdue
          ? `Overdue since ${next.format('MMM D, YYYY h:mm a')}`
          : `Expected in ${window.getPrettyNextCheckin(item.next_checkin)}`;

        return `
            <div style="text-align:left;" class="monospace-dt${isOverdue ? ' is-overdue' : ''}" title="${title}">${next.format('MM/DD/YY')}</div> 
            <div style="text-align:right; width: 69px;" class="monospace-dt${isOverdue ? ' is-overdue' : ''}">${next.format('h:mm a')}</div>
          `;
      }
    },
    readings_every: {
      key: 'readings_every',
      label: 'Frequency',
      description: 'How often a reading is taken / how often battery saver mode uploads the log',
      sortValue: item => readingFrequencySortValue(item),
      value: item => {

        // "1 hour" / "30 mins" normally, "1 hr / 48 hr" when battery saver mode
        // batches the uploads (reading interval / log upload interval)
        const { reading, log } = readingFrequencyLabels(item);

        if(!reading && !log) {
          return '—';
        }

        // keep "/ 48 hr" on one line if the column has to wrap
        const logHtml = log
          ? `<span style="white-space:nowrap;">${reading ? '/ ' : ''}${log}</span>`
          : '';

        return `
          <span title="${describeReadingFrequency(item)}">
            ${reading}${reading && log ? ' ' : ''}${logHtml}
          </span>
        `;

      }
    },
    alerts: {
      key: "alerts",
      label: "Alerts",
      sortValue: item => {
        return item.alerts.length;
      },
      value: item => {

        const alertTypes = [
          {
            key: "temperature",
            value: "Temp",
          },
          {
            key: "humidity",
            value: "Humidity",
          },
          {
            key: "probe_temp",
            value: probeLabel(),
          },
          {
            key: "battery",
            value: "Low Battery",
          },
          {
            key: "power",
            value: "Lost Power",
          },
          {
            key: "connection",
            value: "Offline",
          },

        ]

        let html = "";

        for(let alertType of alertTypes) {

          // red while the Resolution Center has a card open for it, green otherwise
          // (the alerts' own state until the Center has answered)
          let color = dashboard.alertIconColor(item, alertType.key);
          const open = dashboard.hasOpenCard(item, alertType.key);
          let message = open === null ? "" : (open ? "A Resolution Center card is open" : "No open Resolution Center card");

          if(alertType.key === 'connection' && item.offline == 1) {
            color = "red";
            message = "This sensor is offline";
          }

          if(alertType.key === 'battery' && window.getBatteryPercentageForSensor(item) < 20) {
            color = "red";
            message = "This sensor has low batteries";
          }

          html += `
            <span class="alerts-list-item" data-sensor="${item.sensor_id}" data-for="${alertType.key}">${IconsInstance.getIcon(alertType.key, color)}</span>
            `

        }

        return html;

      }
    },
    account: {
      key: 'account_name',
      label: 'Account',
      sortValue: item => item.account_name.toLowerCase(),
      value: item => {
        return item.account_name;
      }
    },
    owner_name: {
      key: 'owner_name',
      label: ownerNameLabel(),
      description: isPolicyHolderEnterprise()
        ? 'The name the account gave itself in the Tempstick app: its business, or the person. Where it has not, the Account name is shown (unless that is an email address)'
        : 'The name the account gave itself in the Tempstick app: its business, or the person',
      sortValue: item => ownerNameFor(item).name.toLowerCase(),
      value: item => {
        const owner = ownerNameFor(item);

        if (!owner.name) {
          return '&mdash;';
        }

        // a borrowed name says so on hover
        return owner.inherited
          ? `<span title="From the Account name: this account has not named itself in the Tempstick app">${escapeHtml(owner.name)}</span>`
          : escapeHtml(owner.name);
      },
    },
    status: {
      key: 'status',
      label: 'Status',
      sortValue: item => item.offline,
      value: item => {
        if(item.offline == 1) {
          return `
            <div class="reading">
              Offline
              <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="width:12px; display: inline-block;">
                <line x1="4" y1="4" x2="16" y2="16" stroke="red" stroke-width="4" />
                <line x1="16" y1="4" x2="4" y2="16" stroke="red" stroke-width="4" />
              </svg>
            </div>
            `
        } else {
          return `
            Online
            <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="width:10px; display: inline-block;">
              <circle cx="10" cy="10" r="10" fill="#94bd46" />
            </svg>
            `
        }
      }
    },
    temp: {
      key: 'temp',
      label: 'Temp',
      sortValue: item => item.last_temp,
      value: item => {
        // wrap the numeric text in a span.reading-value
        const iconHtml = dashboard.getAlertStatusIcon(item, "temperature");
        const tempText = window.celsiusToUserTemp(item.last_temp, 1) + '&deg;' + currentUser.temp_pref;
        return `
          ${iconHtml}
          <span class="reading-value temp-value ${readingAlarmClass(dashboard, item, "temperature")}">
            ${tempText}
          </span>
        `;
      }
    },
    probe_temp: {
      key: 'probe_temp',
      label: probeLabel(),
      sortValue: item => item.last_tcTemp && item.last_tcTemp !== "n" ? parseFloat(item.last_tcTemp) : -99999,
      value: item => {

        if(!item.sensor_id.startsWith("EX")) {
          return "—"
        }

        // TODO:
        // handle v3 enabled but defective (half baked probe data?)

        const iconHtml = dashboard.getAlertStatusIcon(item, "probe_temp");

        if(item.TC_M == 1) {
          if (item.last_tcTemp !== "n" && !isNaN(item.last_tcTemp)) {
            const ptText = window.celsiusToUserTemp(item.last_tcTemp, 1) + '&deg;' + currentUser.temp_pref;
            return `
            ${iconHtml}
            <span class="reading-value probe-value ${readingAlarmClass(dashboard, item, "probe_temp")}">
              ${ptText}
            </span>
          `;
          } else {
            return `
            ${iconHtml} No Data
            `
          }
        } else {
          return `
            ${iconHtml} Not Detected
            `
        }
      }
    },
    humidity: {
      key: 'humidity',
      label: 'Humidity',
      sortValue: item => item.last_humidity,
      value: item => {
        const iconHtml = dashboard.getAlertStatusIcon(item, "humidity");
        const last_humidity = parseFloat(item.last_humidity);
        const humText = roundTo(last_humidity, 1) + '%';
        return `
          ${iconHtml}
          <span class="reading-value humidity-value ${readingAlarmClass(dashboard, item, "humidity")}">
            ${humText}
          </span>
        `;
      }
    },
    heat_index: {
      key: 'heat_index',
      label: 'Heat Index',
      sortValue: item => {

        const temp = celsiusToFahrenheit(item.last_temp);
        const humidity = item.last_humidity;

        const heatIndexF = getHeatIndex(temp, humidity);

        if(window.getUserTempPref() === 'C') {
          return `${fahrenheitToCelsius(heatIndexF, 1)}&deg;${currentUser.temp_pref}`;
        } else {
          return `${roundTo(heatIndexF, 1)}&deg;${currentUser.temp_pref}`;
        }

      },
      value: item => {

        const temp = celsiusToFahrenheit(item.last_temp);
        const humidity = item.last_humidity;

        const heatIndexF = getHeatIndex(temp, humidity);

        if(window.getUserTempPref() === 'C') {
          return `${fahrenheitToCelsius(heatIndexF, 1)}&deg;${currentUser.temp_pref}`;
        } else {
          return `${roundTo(heatIndexF, 1)}&deg;${currentUser.temp_pref}`;
        }

      }
    },
    dew_point: {
      key: 'dew_point',
      label: 'Dewpoint',
      sortValue: item => {
        return window.getDewpoint(item.last_humidity, item.last_temp);
      },
      value: item => {
        return window.getDewpoint(item.last_humidity, item.last_temp);
      }
    },
    power: {
      key: 'power',
      label: 'Power',
      sortValue: item => {
        return item.last_pwr == 1 ? "USB" : "Battery"
      },
      value: item => {
        return item.last_pwr == 1 ? "USB" : "Battery"
      }
    },
    battery: {
      key: 'battery',
      label: 'Battery',
      // return a numeric percentage for correct sorting
      sortValue: item => {
        // prefer explicit battPct if available
        if (!isUndefined(item.last_battPct) && !isNaN(item.last_battPct)) {
          return parseFloat(item.last_battPct);
        }
        // otherwise compute from voltage (assumes getBatteryPercentage returns a number)
        const pct = window.getBatteryPercentage(item.last_voltage);
        return typeof pct === 'string' ? parseFloat(pct) : pct;
      },
      value: item => {
        // leave your existing display logic unchanged
        if (!isUndefined(item.last_battPct)) {
          return window.getBatteryIconPct(item.last_battPct);
        }
        return window.getBatteryPercentage(item.last_voltage) + "%";
      }
    },
    rssi: {
      key: 'rssi',
      label: 'Signal Strength',
      sortValue: item => item.rssi,
      value: item => {
        return window.getSignalStrengthIcon(item.rssi);
      }
    },
    pipe_strap_location: {
      key: 'pipe_strap_location',
      label: 'Pipe Type',
      sortValue: item => formatPipeStrapLocation(item).toLowerCase(),
      value: item => questionnaireValue(formatPipeStrapLocation(item)),
    },
    sprinkler_system_type: {
      key: 'sprinkler_system_type',
      label: 'Sprinkler Type',
      sortValue: item => formatSprinklerSystemType(item).toLowerCase(),
      value: item => questionnaireValue(formatSprinklerSystemType(item)),
    },
    zip_code: {
      key: 'zip_code',
      label: 'Zip Code',
      sortValue: item => formatZipCode(item).toLowerCase(),
      value: item => questionnaireValue(formatZipCode(item)),
    },
  }

}

export default sensorData;
